import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { agentsApi } from "@/api/agents";
import { queryKeys } from "@/lib/query-keys";
import { agentRoleLabel } from "@/lib/agent-display";
import {
  ArrowRight,
  Bot,
  ChevronDown,
  ChevronRight,
  Cpu,
  Globe,
  Layers,
  Plus,
  Search,
  Server,
  Sparkles,
  Terminal,
  Trash2,
  Webhook,
} from "lucide-react";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { MobileAgentsList } from "@/pages/mobile/MobileAgentsList";

const statusVariant: Record<string, "success" | "warning" | "danger" | "default" | "secondary"> = {
  active: "success",
  running: "success",
  idle: "secondary",
  paused: "warning",
  pending_approval: "warning",
  error: "danger",
  terminated: "danger",
};

const adapterIcons: Record<string, typeof Globe> = {
  openclaw_gateway: Globe,
  anthropic_native: Sparkles,
  claude_code: Terminal,
  process: Cpu,
  http: Webhook,
};

const adapterLabels: Record<string, string> = {
  openclaw_gateway: "OpenClaw",
  anthropic_native: "Anthropic Native",
  claude_code: "Claude Code",
  process: "Process",
  http: "HTTP",
};

type AgentsProps = {
  embedded?: boolean;
  onOpenAgent?: (agentId: string) => void;
};

export function Agents({ embedded = false, onOpenAgent }: AgentsProps = {}) {
  const isMobile = useIsMobile();
  if (isMobile && !embedded) return <MobileAgentsList />;
  return <AgentsDesktop embedded={embedded} onOpenAgent={onOpenAgent} />;
}

function AgentsDesktop({ embedded = false, onOpenAgent }: AgentsProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tenantId, isTenantAdmin, canManageAnyAgent } = useTenant();
  const { data: agents = [], isLoading } = useAgentsByCompany(tenantId);
  const [showNewMenu, setShowNewMenu] = useState(false);
  const [showParentPicker, setShowParentPicker] = useState(false);
  const [search, setSearch] = useState("");
  // Only tenant admins can spin up new VMs (cost implication). Agent
  // admins can add sub-agents on existing parent instances. Standard
  // users see no creation affordances at all.
  const canCreateInstance = !embedded && isTenantAdmin;
  const canCreateSubagent = !embedded && canManageAnyAgent;
  const canCreateAny = canCreateInstance || canCreateSubagent;
  const openAgent = (agentId: string) => {
    if (onOpenAgent) {
      onOpenAgent(agentId);
      return;
    }
    navigate(`/tenants/${tenantId}/agents/${agentId}`);
  };

  const deleteMutation = useMutation({
    mutationFn: (id: string) => agentsApi.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.agents.byCompany(tenantId) }),
  });

  // Separate primary agents (instances) from sub-agents. Orphan sub-
  // agents — granted subagents whose parent VM the user can't see (the
  // standard_user / agent_admin filtered-list case) — get promoted to
  // the top of the primary list so they render at all. Without this
  // promotion the row would silently disappear because the page only
  // iterates primaries and nests subagents under them.
  const visibleIds = new Set(agents.map((a) => a.id));
  const primaryAgents = agents.filter(
    (a) => !a.parentAgentId || !visibleIds.has(a.parentAgentId),
  );
  const subAgents = agents.filter(
    (a) => !!a.parentAgentId && visibleIds.has(a.parentAgentId),
  );
  const normalizedSearch = search.trim().toLowerCase();
  const matchesSearch = (agent: (typeof agents)[number]) => {
    if (!normalizedSearch) return true;
    const model = agent.adapterConfig?.model as string | undefined;
    return [
      agent.name,
      agentRoleLabel(agent),
      agent.status,
      adapterLabels[agent.adapterType] ?? agent.adapterType,
      model,
    ].some((value) => value?.toLowerCase().includes(normalizedSearch));
  };
  const filteredPrimaryAgents = primaryAgents.filter(
    (agent) =>
      matchesSearch(agent) ||
      subAgents.some(
        (subAgent) =>
          subAgent.parentAgentId === agent.id && matchesSearch(subAgent),
      ),
  );

  return (
    <CenterPanel title="Agents">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-text-secondary">
          {primaryAgents.length} instance{primaryAgents.length !== 1 ? "s" : ""}
          {subAgents.length > 0 && ` · ${subAgents.length} sub-agent${subAgents.length !== 1 ? "s" : ""}`}
        </p>

        {/* New agent dropdown — hidden entirely for standard users. */}
        {canCreateAny && (
        <div className="relative">
          <Button size="sm" onClick={() => setShowNewMenu(!showNewMenu)}>
            <Plus size={16} className="mr-1.5" />
            New Agent
            <ChevronDown size={14} className="ml-1.5" />
          </Button>

          {showNewMenu && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => { setShowNewMenu(false); setShowParentPicker(false); }} />
              <div className="absolute right-0 top-full z-40 mt-1.5 w-[min(320px,calc(100vw-2rem))] rounded-xl border border-border bg-background shadow-xl">
                <div className="p-1.5">
                  {/* "Deploy New Instance" provisions a VM. Cost +
                       infrastructure implications → tenant_admin only. */}
                  {canCreateInstance && (
                  <button
                    onClick={() => {
                      setShowNewMenu(false);
                      navigate(`/tenants/${tenantId}/agents/new`);
                    }}
                    className="flex w-full items-start gap-3 rounded-lg px-3 py-3 text-left transition-colors hover:bg-surface-hover"
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent/10">
                      <Server size={18} className="text-accent" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-text-primary">Deploy New Instance</p>
                      <p className="text-xs text-text-muted mt-0.5">
                        Provisions a new Azure VM with its own runtime, networking, and primary agent.
                      </p>
                    </div>
                  </button>
                  )}

                  {canCreateInstance && canCreateSubagent && (
                    <div className="mx-3 my-1 h-px bg-border" />
                  )}

                  {/* Sub-agent: show parent picker */}
                  {!showParentPicker ? (
                    <button
                      onClick={() => {
                        if (primaryAgents.length === 1) {
                          // Only one instance — go directly
                          setShowNewMenu(false);
                          setShowParentPicker(false);
                          navigate(`/tenants/${tenantId}/agents/${primaryAgents[0].id}/subagents/new`);
                        } else if (primaryAgents.length === 0) {
                          // No instances — can't create sub-agent
                        } else {
                          setShowParentPicker(true);
                        }
                      }}
                      disabled={primaryAgents.length === 0}
                      className="flex w-full items-start gap-3 rounded-lg px-3 py-3 text-left transition-colors hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-purple-500/10">
                        <Layers size={18} className="text-purple-500" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-text-primary">Create Sub-Agent</p>
                        <p className="text-xs text-text-muted mt-0.5">
                          {primaryAgents.length === 0
                            ? "Deploy an instance first to add sub-agents."
                            : "Add a sub-agent to an existing instance. Shares the parent's VM and networking."}
                        </p>
                      </div>
                      {primaryAgents.length > 1 && (
                        <ChevronRight size={14} className="text-text-muted mt-2 shrink-0" />
                      )}
                    </button>
                  ) : (
                    <div>
                      <button
                        onClick={() => setShowParentPicker(false)}
                        className="flex items-center gap-1.5 px-3 py-2 text-xs text-text-muted hover:text-text-primary transition-colors"
                      >
                        <ArrowRight size={10} className="rotate-180" />
                        Back
                      </button>
                      <p className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                        Select parent instance
                      </p>
                      <div className="max-h-[200px] overflow-y-auto">
                        {primaryAgents.map((agent) => (
                          <button
                            key={agent.id}
                            onClick={() => {
                              setShowNewMenu(false);
                              setShowParentPicker(false);
                              navigate(`/tenants/${tenantId}/agents/${agent.id}/subagents/new`);
                            }}
                            className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-surface-hover"
                          >
                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-accent/10">
                              <Server size={14} className="text-accent" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-text-primary truncate">{agent.name}</p>
                              <p className="text-[11px] text-text-muted">{agentRoleLabel(agent)}</p>
                            </div>
                            <Badge variant={statusVariant[agent.status] ?? "default"} className="text-[9px]">
                              {agent.status}
                            </Badge>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        )}
      </div>

      {!isLoading && agents.length > 0 && (
        <div className="relative mb-4">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
          />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search agents..."
            aria-label="Search agents"
            className="w-full rounded-md border border-border bg-surface py-2 pl-8 pr-3 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-border"
          />
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-text-muted">Loading agents...</p>
      ) : agents.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Bot size={40} className="mb-3 text-text-muted" />
            <p className="text-sm text-text-secondary">No agents yet.</p>
            {canCreateInstance && (
              <Button
                size="sm"
                className="mt-3"
                onClick={() => navigate(`/tenants/${tenantId}/agents/new`)}
              >
                <Plus size={14} className="mr-1.5" />
                Deploy your first instance
              </Button>
            )}
          </CardContent>
        </Card>
      ) : filteredPrimaryAgents.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-10 text-center">
            <Search size={32} className="mb-3 text-text-muted" />
            <p className="text-sm text-text-secondary">
              No agents match &ldquo;{search.trim()}&rdquo;.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-4" data-tour="agents-list">
          {/* Primary agents (instances) */}
          {filteredPrimaryAgents.map((agent) => {
            const AdapterIcon = adapterIcons[agent.adapterType] ?? Bot;
            const model = agent.adapterConfig?.model as string | undefined;
            const parentMatchesSearch = matchesSearch(agent);
            const agentSubs = subAgents.filter(
              (subAgent) =>
                subAgent.parentAgentId === agent.id &&
                (parentMatchesSearch || matchesSearch(subAgent)),
            );
            return (
              <div key={agent.id}>
                <Card
                  className="cursor-pointer transition-colors hover:border-accent/40"
                  onClick={() => openAgent(agent.id)}
                >
                  <CardContent className="flex items-center justify-between py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10">
                        <AdapterIcon size={20} className="text-accent" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-text-primary">{agent.name}</p>
                          <Badge variant="secondary" className="text-[9px] py-0 px-1.5">
                            <Server size={9} className="mr-0.5" />
                            Instance
                          </Badge>
                        </div>
                        <p className="text-xs text-text-muted flex items-center gap-1">
                          <span>{agentRoleLabel(agent)}</span>
                          {agent.adapterType && agent.adapterType !== "process" && (
                            <span> · {adapterLabels[agent.adapterType] ?? agent.adapterType}</span>
                          )}
                          {model && <span> · {model}</span>}
                          {agentSubs.length > 0 && (
                            <span className="inline-flex items-center gap-0.5 ml-1 text-accent">
                              <Layers size={10} /> {agentSubs.length} sub-agent{agentSubs.length !== 1 ? "s" : ""}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant={statusVariant[agent.status] ?? "default"}>
                        {agent.status}
                      </Badge>
                      {/* Deleting a top-level agent destroys its VM —
                          tenant_admin only (matches server guard). */}
                      {canCreateInstance && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); deleteMutation.mutate(agent.id); }}
                        >
                          <Trash2 size={14} />
                        </Button>
                      )}
                      <ArrowRight size={16} className="text-text-muted" />
                    </div>
                  </CardContent>
                </Card>

                {/* Sub-agents nested under this instance */}
                {agentSubs.length > 0 && (
                  <div className="ml-6 mt-1 flex flex-col gap-1 border-l-2 border-accent/20 pl-4">
                    {agentSubs.map((sub) => {
                      const SubIcon = adapterIcons[sub.adapterType] ?? Bot;
                      const subModel = sub.adapterConfig?.model as string | undefined;
                      return (
                        <Card
                          key={sub.id}
                          className="cursor-pointer transition-colors hover:border-accent/40 border-dashed"
                          onClick={() => openAgent(sub.id)}
                        >
                          <CardContent className="flex items-center justify-between py-3">
                            <div className="flex items-center gap-2.5">
                              <div className="flex h-8 w-8 items-center justify-center rounded-md bg-purple-500/10">
                                <SubIcon size={16} className="text-purple-500" />
                              </div>
                              <div>
                                <div className="flex items-center gap-1.5">
                                  <p className="text-[13px] font-medium text-text-primary">{sub.name}</p>
                                  <span className="inline-flex items-center gap-0.5 rounded-full bg-purple-500/10 px-1.5 py-0.5 text-[9px] text-purple-600 dark:text-purple-400 font-medium">
                                    <Layers size={8} />
                                    Sub-Agent
                                  </span>
                                </div>
                                <p className="text-[11px] text-text-muted">
                                  {agentRoleLabel(sub)}
                                  {subModel && <> · {subModel}</>}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant={statusVariant[sub.status] ?? "default"} className="text-[10px]">
                                {sub.status}
                              </Badge>
                              {canCreateSubagent && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0"
                                  onClick={(e) => { e.stopPropagation(); deleteMutation.mutate(sub.id); }}
                                >
                                  <Trash2 size={12} />
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </CenterPanel>
  );
}
